import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Brand } from "@/components/Brand";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import VersionBanner from "@/components/VersionBanner";
import VersionTag from "@/components/VersionTag";
import { visibleTools } from "@/lib/partnerTools";
import { catalogFor } from "@/lib/productCatalog";
import { TrattativaProvider } from "@/context/TrattativaContext";
import { TopNav, GlobalSearch, CartButton, ProductLink } from "@/components/PartnerNav";
import { LayoutGrid, FolderOpen, FileCheck2, LogOut, Users, Menu, X, Wallet, FilePlus2 } from "lucide-react";

const RAIL = [
  { to: "/", label: "Home", icon: LayoutGrid, end: true, tid: "home" },
  { to: "/preventivi?vista=aperte", label: "Pratiche", icon: FolderOpen, tid: "pratiche-aperte" },
  { to: "/anagrafiche", label: "Anagrafiche", icon: Users, tid: "anagrafiche" },
  { to: "/proposte", label: "Proposte", icon: FileCheck2, tid: "proposte-emesse" },
  { to: "/provvigioni", label: "Provvigioni", icon: Wallet, tid: "provvigioni" },
];

function Rail() {
  return (
    <aside className="sticky top-[93px] z-20 hidden h-[calc(100vh-93px)] w-20 shrink-0 flex-col items-center gap-1.5 border-r border-border bg-white pt-5 md:flex" data-testid="partner-rail">
      {RAIL.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.tid}`} title={n.label}
          className={({ isActive }) => `flex w-16 flex-col items-center gap-1 rounded-xl py-2.5 text-[10px] font-semibold transition-colors ${isActive ? "bg-forest text-white" : "text-forest/60 hover:bg-sage hover:text-forest"}`}>
          <n.icon className="h-5 w-5" strokeWidth={1.75} />{n.label}
        </NavLink>
      ))}
      <div className="mt-auto pb-3"><VersionTag /></div>
    </aside>
  );
}

function MobileMenu({ user, tools, onClose }) {
  return (
    <div className="fixed inset-0 z-50 md:hidden">
      <div className="absolute inset-0 bg-forest/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute inset-y-0 left-0 w-80 overflow-y-auto bg-white p-4 shadow-xl">
        <div className="mb-4 flex items-center justify-between"><Brand /><Button variant="ghost" size="icon" onClick={onClose} data-testid="sidebar-close"><X className="h-5 w-5" /></Button></div>
        {[{ to: "/pratica/nuova", label: "Nuova trattativa", icon: FilePlus2 }, ...RAIL].map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-forest hover:bg-sage"><n.icon className="h-4 w-4" />{n.label}</NavLink>
        ))}
        {catalogFor(user).map((l) => l.items.length > 0 && (
          <div key={l.key} className="mt-4">
            <div className="px-3 text-[11px] font-bold uppercase tracking-wide" style={{ color: l.color }}>{l.short}</div>
            <div className="px-3">{l.items.map((p) => <ProductLink key={p.code} p={p} onClick={onClose} />)}</div>
          </div>
        ))}
        {tools.length > 0 && (
          <div className="mt-4" data-testid="nav-tools">
            <div className="px-3 text-[11px] font-bold uppercase tracking-wide text-forest/50">Strumenti</div>
            {tools.map((t) => <NavLink key={t.key} to={t.to} onClick={onClose} data-testid={`mobile-tool-${t.key}`} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-forest hover:bg-sage"><t.icon className="h-4 w-4" />{t.label}</NavLink>)}
          </div>
        )}
      </aside>
    </div>
  );
}

export default function PartnerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [hasLandings, setHasLandings] = useState(false);
  const [leadCount, setLeadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); navigate("/login"); };

  useEffect(() => {
    api.get("/landings/mine").then(({ data }) => setHasLandings((data || []).length > 0)).catch(() => {});
    api.get("/intakes/nuovi-count").then(({ data }) => setLeadCount(data?.count || 0)).catch(() => {});
  }, []);

  const tools = visibleTools(user)
    .flatMap((t) => (t.key === "marketing" && hasLandings ? [t, { ...t, key: "landing", label: "Le mie landing", to: "/landing" }] : [t]))
    .map((t) => ({ ...t, badge: t.key === "link_cliente" ? leadCount : 0 }));

  return (
    <TrattativaProvider>
      <div className="min-h-screen bg-bone">
        <header className="sticky top-0 z-30 bg-white shadow-[0_1px_0_hsl(var(--border))]">
          <div className="mx-auto flex max-w-6xl items-center gap-6 px-4 py-3 md:px-5">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open"><Menu className="h-5 w-5" /></Button>
            <NavLink to="/" data-testid="header-home"><Brand /></NavLink>
            <GlobalSearch user={user} />
            <div className="ml-auto flex items-center gap-2 md:ml-0">
              <NotificationBell />
              <CartButton />
              <div className="hidden items-center gap-2 pl-2 lg:flex">
                <div className="grid h-9 w-9 place-items-center rounded-full bg-forest text-sm font-bold text-white">{(user?.name || "?")[0]}</div>
                <div className="leading-tight">
                  <div className="text-sm font-medium text-forest">{user?.name}</div>
                  <div className="text-[11px] text-muted-foreground">{ROLE_LABELS[user?.role]} · {user?.codice_collab}</div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={doLogout} data-testid="logout-button" title="Esci"><LogOut className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="px-4 pb-3 md:hidden"><GlobalSearch user={user} mobile /></div>
          <TopNav user={user} tools={tools} />
        </header>
        {open && <MobileMenu user={user} tools={tools} onClose={() => setOpen(false)} />}
        <div className="flex">
          <Rail />
          <div className="min-w-0 flex-1">
            <VersionBanner />
            <main className="mx-auto max-w-6xl px-4 py-6 md:px-8 md:py-8"><Outlet /></main>
            <ComplianceNotice variant="footer" />
          </div>
        </div>
      </div>
    </TrattativaProvider>
  );
}
